import React from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { APP_CONFIG, formatDate as formatDateGlobal } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { deleteQuotationEvaluation } from '../../../services/mutations';
import { fetcher } from '../../../services/fetcher';
import { supplyListMoneyTypographyClass, type SupplyListMoneyVariant } from '../supplyListMoneyDisplay';
import { supplyBlockedActionMessage } from '../blockingMessages';
import type { QuotationEvaluation, QuotationEvaluationSummaryByQuote } from './useData';

type ListColumnsContext = {
  listTableData?: QuotationEvaluation[];
};

function supplierLabelFromByQuote(b: QuotationEvaluationSummaryByQuote | undefined): string {
  if (!b?.supplier) return '';
  return b.supplier.name?.trim() || b.supplier.code?.trim() || '';
}

/** True if this slot should appear on the list: at least one visible row has a supplier for that quote slot. */
function slotHasSupplierOnPage(items: QuotationEvaluation[], slot: number): boolean {
  return items.some((item) => supplierLabelFromByQuote(item.summary?.byQuote?.find((x) => x.slot === slot)) !== '');
}

/** Uses visible page rows; thead is shared so multiple suppliers in one slot show a compact combined label + title. */
function groupHeaderSupplierLabel(items: QuotationEvaluation[], slot: number): React.ReactNode {
  const labels = new Set<string>();
  for (const item of items) {
    const b = item.summary?.byQuote?.find((x) => x.slot === slot);
    const label = supplierLabelFromByQuote(b);
    if (label) labels.add(label);
  }
  if (labels.size === 0) return `Supplier ${slot}`;
  const arr = Array.from(labels);
  if (arr.length === 1) return arr[0];
  const title = arr.join(', ');
  if (arr.length === 2) {
    return (
      <span title={title}>
        {arr[0]} · {arr[1]}
      </span>
    );
  }
  return (
    <span title={title}>
      {arr[0]} · {arr[1]} (+{arr.length - 2})
    </span>
  );
}

const fmt2 = (n: string | number | null | undefined) => {
  if (n == null || n === '') return '—';
  const v = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

export const moduleConfig = {
  module: {
    title: 'Quotation evaluation',
    permissionModuleKey: 'quotation_evaluations',
    route: {
      list: '/supplies/quotation-evaluations',
      new: '/supplies/quotation-evaluations/new',
      newForCase: (caseId: number) => `/supplies/quotation-evaluations/new?caseId=${caseId}`,
      detail: (id: number) => `/supplies/quotation-evaluations/${id}`,
      edit: (id: number) => `/supplies/quotation-evaluations/${id}/edit`},
    api: {
      list: '/quotation-evaluations',
      detail: (id: number) => `/quotation-evaluations/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by reference or case…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['vessel', 'ref', 'evaluationDate', 'caseRef']},
  ],
  filters: {
    fields: [
      {
        key: 'vessel',
        label: 'Vessel',
        type: 'select' as const,
        placeholder: 'All vessels',
        getValue: (item: QuotationEvaluation) => item.supplyCase?.vessel?.vesselName?.trim() || '',
        options: []},
      {
        key: 'ref',
        label: 'Reference',
        placeholder: 'All references',
        getValue: (item: QuotationEvaluation) => item.ref || '',
        options: []},
      {
        key: 'evaluationDate',
        label: 'Date',
        type: 'date' as const,
        placeholder: 'All dates',
        getValue: (item: QuotationEvaluation) => item.evaluationDate || '',
        options: []},
      {
        key: 'caseRef',
        label: 'Case',
        type: 'select' as const,
        placeholder: 'All cases',
        getValue: (item: QuotationEvaluation) => item.supplyCase?.ref || '',
        options: []},
    ],
    applyFilter: (
      items: QuotationEvaluation[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>,
    ) => {
      const asArray = (v: string[] | string | undefined): string[] =>
        Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : [];
      const toDateOnly = (s?: string | null): string => (s ? String(s).slice(0, 10) : '');
      return items.filter((item) => {
        const vesselFilters = asArray(filters.vessel);
        if (vesselFilters.length) {
          const vesselName = (item.supplyCase?.vessel?.vesselName || '').toLowerCase();
          if (!vesselFilters.some((f) => vesselName.includes(String(f).toLowerCase()))) return false;
        }

        const refFilters = asArray(filters.ref);
        if (refFilters.length) {
          const v = item.ref || '';
          if (!refFilters.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }

        const evalDateFilter = dateRanges?.evaluationDate;
        if (evalDateFilter?.start || evalDateFilter?.end) {
          const itemDateRaw = item.evaluationDate ? new Date(item.evaluationDate) : null;
          if (!itemDateRaw || Number.isNaN(itemDateRaw.getTime())) return false;
          if (evalDateFilter.start && itemDateRaw < evalDateFilter.start) return false;
          if (evalDateFilter.end && itemDateRaw > evalDateFilter.end) return false;
        } else {
          const evalDateValues = asArray(filters.evaluationDate);
          if (evalDateValues.length) {
            const itemDate = toDateOnly(item.evaluationDate);
            if (!evalDateValues.some((f) => itemDate.includes(String(f).slice(0, 10)))) return false;
          }
        }

        const caseFilters = asArray(filters.caseRef);
        if (caseFilters.length) {
          const c = (item.supplyCase?.ref || '').toLowerCase();
          if (!caseFilters.some((f) => c.includes(String(f).toLowerCase()))) return false;
        }
        return true;
      });
    },
    applySearch: (items: QuotationEvaluation[], searchTerm: string) => {
      const q = searchTerm.toLowerCase();
      return items.filter((item) => {
        const parts = [
          item.ref,
          item.supplyCase?.ref,
          String(item.id),
          String(item.caseId),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return parts.includes(q);
      });
    }},
  toolbar: {
    actions: []},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: QuotationEvaluation) => item.id,
  },
  emptyStates: {
    noData: 'Create a quotation evaluation to get started.',
    noSearchResults: 'No evaluations match your search.',
    noFilterResults: 'No evaluations match the filters.',
  },
  delete: {
    title: 'Delete quotation evaluation',
    message: 'Delete this quotation evaluation?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete quotation evaluation.',
    handler: async (id: number) => {
      await deleteQuotationEvaluation(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  form: {
    new: { title: 'New quotation evaluation' },
    edit: { title: 'Edit quotation evaluation' }},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

/** Path must stay in sync with `moduleConfig.route.caseView` in `supply-cases/config.tsx`. */
function quotationEvaluationCaseIdForView(row: QuotationEvaluation | undefined): number | null {
  const id = row?.supplyCase?.id ?? row?.caseId ?? 0;
  return id > 0 ? id : null;
}

const EvaluationCaseColumnCell: React.FC<{ row: QuotationEvaluation }> = ({ row }) => {
  const { colors } = useTheme();
  const caseId = quotationEvaluationCaseIdForView(row);
  if (caseId == null) {
    return (
      <div className="max-w-[16rem] py-0.5">
        <span className={cn('text-sm', colors.textSecondary)}>—</span>
      </div>
    );
  }
  const displayRef = row.supplyCase?.ref?.trim() || `Case #${caseId}`;
  const href = `/supplies/supply-cases/${caseId}/case-view`;
  const iconBtnClass =
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900 dark:text-indigo-400 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-300';

  return (
    <div className="flex max-w-[16rem] items-center gap-0.5 py-0.5">
      <Link
        to={href}
        className={iconBtnClass}
        title="Open case view"
        aria-label="Open case view"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      </Link>
      <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)} title={displayRef}>
        {displayRef}
      </span>
    </div>
  );
};

type SupplierMetricKey = 'items' | 'expenses' | 'grand' | 'grandUsd';

function evaluationMetricVariant(metric: SupplierMetricKey): SupplyListMoneyVariant {
  switch (metric) {
    case 'expenses':
      return 'expenses';
    case 'grand':
      return 'grand';
    case 'grandUsd':
      return 'usd';
    default:
      return 'items';
  }
}

const SupplierMetricCell: React.FC<{
  row: QuotationEvaluation;
  slot: number;
  metric: SupplierMetricKey;
}> = ({ row, slot, metric }) => {
  const { colors } = useTheme();
  const summary = row.summary;
  const b = summary?.byQuote?.find((x) => x.slot === slot);
  const canUsd = summary?.canCompareInUsd ?? false;
  const highlight = b?.isLowestTotalUsd ?? false;

  if (!b) {
    return (
      <span className={cn('block w-full min-w-[6.75rem] text-right text-sm', colors.textSecondary)}>—</span>
    );
  }

  let value: number | null = null;
  if (metric === 'items') value = b.lineItemsSubtotal;
  else if (metric === 'expenses') value = b.expensesSubtotal;
  else if (metric === 'grand') value = b.grandTotal;
  else value = canUsd && b.grandTotalUsd != null ? b.grandTotalUsd : null;

  const emphasizeTotals = highlight && (metric === 'grand' || metric === 'grandUsd');
  const typography = supplyListMoneyTypographyClass(
    evaluationMetricVariant(metric),
    colors,
    metric === 'grandUsd' ? value != null : undefined,
  );

  return (
    <div
      className={cn(
        'flex w-full min-w-[6.75rem] justify-end py-1 pl-2 pr-1',
        highlight &&
          'border-l-[3px] border-emerald-500 bg-gradient-to-r from-emerald-500/[0.07] to-transparent dark:border-emerald-400 dark:from-emerald-400/[0.10]',
      )}
    >
      <span
        className={cn(
          'inline-flex items-baseline justify-end gap-0.5',
          typography,
          emphasizeTotals && 'text-emerald-800 dark:text-emerald-300',
        )}
      >
        {highlight && metric === 'grandUsd' && value != null ? (
          <span
            className="select-none text-[0.65rem] leading-none text-emerald-600 dark:text-emerald-400"
            title="Lowest comparable total"
            aria-hidden
          >
            ★
          </span>
        ) : null}
        {metric === 'grandUsd' && value == null ? '—' : fmt2(value as number)}
      </span>
    </div>
  );
};

const SUPPLIER_SLOT_COUNT = 5;

const SUPPLIER_METRICS: { key: SupplierMetricKey; header: string }[] = [
  { key: 'items', header: 'Items Total' },
  { key: 'expenses', header: 'Expenses Total' },
  { key: 'grand', header: 'Grand Total' },
  { key: 'grandUsd', header: 'Grand Total ($)' },
];

export const getColumns = (
  formatDate: (dateString?: string) => string,
  _arg2?: unknown,
  _colors?: unknown,
  context?: ListColumnsContext,
): Column<QuotationEvaluation>[] => {
  const pageRows = context?.listTableData ?? [];
  const supplierSlotsToShow = Array.from({ length: SUPPLIER_SLOT_COUNT }, (_, i) => i + 1).filter((slot) =>
    slotHasSupplierOnPage(pageRows, slot),
  );
  return [
    {
      key: 'vessel',
      header: 'Vessel',
      sortable: true,
      render: (_v, row) => <TextCell value={row?.supplyCase?.vessel?.vesselName ?? ''} />},
    { key: 'ref', header: 'Reference', sortable: true, render: (v, row) => <TextCell value={v ?? row?.ref} /> },
    { key: 'evaluationDate', header: 'Date', sortable: true, render: (v) => formatDate(v) },
    {
      key: 'supplyCase.ref',
      header: 'Case',
      sortable: true,
      maxWidth: '16rem',
      render: (_v, row) => (row ? <EvaluationCaseColumnCell row={row} /> : <TextCell value="" />)},
    ...supplierSlotsToShow.flatMap((slot) => {
      const groupId = `qe-supplier-slot-${slot}`;
      const groupHeader = groupHeaderSupplierLabel(pageRows, slot);
      return SUPPLIER_METRICS.map((m) => ({
        key: `summary.slot${slot}.${m.key}`,
        header: m.header,
        align: 'right' as const,
        columnGroup: {
          id: groupId,
          header: groupHeader},
        sortable: false,
        render: (_v: unknown, row?: QuotationEvaluation) =>
          row ? <SupplierMetricCell row={row} slot={slot} metric={m.key} /> : null}));
    }),
    { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
  ];
};

const TableActionsCell: React.FC<{
  item: QuotationEvaluation;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  const { showError } = useNotification();
  const { data: purchaseOrdersRes } = useSWR<any>('/supply-purchase-orders?page=1&limit=1000', fetcher);

  const hasCaseAssigned = (item.caseId ?? 0) > 0;
  const hasOrderInCase = React.useMemo(() => {
    const rows = Array.isArray(purchaseOrdersRes) ? purchaseOrdersRes : purchaseOrdersRes?.data ?? [];
    const caseId = item.caseId ?? 0;
    if (caseId <= 0) return false;
    return rows.some((po: any) => Number(po?.caseId) === caseId);
  }, [purchaseOrdersRes, item.caseId]);

  const deleteEnabled = canDelete && !hasCaseAssigned && !hasOrderInCase;
  const editEnabled = canEdit && !hasOrderInCase;

  const onDeleteBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasOrderInCase) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('QE_DELETE_CASE_HAS_ORDER')}`);
      return;
    }
    if (hasCaseAssigned) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('QE_DELETE_CASE_ASSIGNED')}`);
    }
  };

  const onEditBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasOrderInCase) {
      showError(`Cannot edit: ${supplyBlockedActionMessage('QE_EDIT_CASE_HAS_ORDER')}`);
    }
  };

  return (
    <>
      <div className="flex items-center flex-nowrap">
        <Link
          to={moduleConfig.module.route.detail(item.id)}
          className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="View"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </Link>
        {canEdit && editEnabled ? (
          <Link
            to={moduleConfig.module.route.edit(item.id)}
            className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
            onClick={(e) => e.stopPropagation()}
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </Link>
        ) : canEdit ? (
          <button
            type="button"
            onClick={onEditBlocked}
            className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        ) : null}
        {canDelete && deleteEnabled ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.id);
            }}
            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        ) : canDelete ? (
          <button
            type="button"
            onClick={onDeleteBlocked}
            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        ) : null}
      </div>
    </>
  );
};

export const getTableActions = (
  item: QuotationEvaluation,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};
